/**
 * The single source of "today" and "now" in Dreamliner Hub.
 *
 * The company runs in Pacific time. Deriving a date from toISOString rolls the
 * calendar over at 5pm Pacific, which pushed today's date below the booking
 * form's min attribute and disabled every slot. Nothing in the app should
 * compute a date string any other way.
 */
export const BUSINESS_TZ = "America/Los_Angeles";

const dayFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: BUSINESS_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const clockFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: BUSINESS_TZ,
  hour12: false,
  hour: "2-digit",
  minute: "2-digit",
});

const partsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: BUSINESS_TZ,
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/** The business day, as YYYY-MM-DD. Never toISOString. */
export function todayISO(at: Date = new Date()): string {
  return dayFormatter.format(at);
}

/** The business-local calendar date of any instant, as YYYY-MM-DD. */
export const businessDateOf = (value: Date | string): string =>
  dayFormatter.format(typeof value === "string" ? new Date(value) : value);

/** "HH:MM" in business time. */
export const businessTimeOf = (value: Date | string): string =>
  clockFormatter.format(typeof value === "string" ? new Date(value) : value);

/** Minutes since business-local midnight. */
export function nowMinutesLocal(at: Date = new Date()): number {
  const [h, m] = clockFormatter.format(at).split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Minutes since business-local midnight for any instant. */
export function minutesOfDayLocal(value: Date | string): number {
  return nowMinutesLocal(typeof value === "string" ? new Date(value) : value);
}

/** How far the business timezone sits from UTC at a given instant, in ms. */
function tzOffsetMs(at: Date): number {
  const parts = partsFormatter.formatToParts(at);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUTC = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return asUTC - at.getTime();
}

/**
 * A business-local date plus minute offset, converted to the correct instant.
 * The offset is computed for that specific date, so daylight saving is handled
 * instead of assumed.
 */
export function localDateTimeToUTC(dateISO: string, minutes: number): Date {
  const [y, m, d] = dateISO.split("-").map(Number);
  if (!y || !m || !d) return new Date(NaN);
  const naive = Date.UTC(y, m - 1, d) + minutes * 60000;
  let ts = naive - tzOffsetMs(new Date(naive));
  ts = naive - tzOffsetMs(new Date(ts));
  return new Date(ts);
}

/** "HH:MM" business-local, on a business-local date, as a real instant. */
export const localDateHmToUTC = (dateISO: string, hm: string): Date => {
  const [h, m] = hm.split(":").map(Number);
  return localDateTimeToUTC(dateISO, (h ?? 0) * 60 + (m ?? 0));
};

/** Shifts a YYYY-MM-DD date string by whole days, staying a date string. */
export function addDaysISO(dateISO: string, days: number): string {
  const [y, m, d] = dateISO.split("-").map(Number);
  const shifted = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days));
  return dayFormatter.format(new Date(shifted.getTime() + 12 * 3600_000));
}

/** The same calendar date one year out, for date input bounds. */
export function oneYearOutISO(fromISO: string = todayISO()): string {
  const [y, m, d] = fromISO.split("-").map(Number);
  return `${String((y ?? 1970) + 1).padStart(4, "0")}-${String(m ?? 1).padStart(2, "0")}-${String(d ?? 1).padStart(2, "0")}`;
}

/** Day of week (0 Sunday) for a business-local date string. */
export function weekdayOfISO(dateISO: string): number {
  const [y, m, d] = dateISO.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
}

/** True when the date string is the current business day. */
export const isBusinessToday = (dateISO: string): boolean => dateISO === todayISO();

/* ------------------------------------------------------------------------ *
 * THE ONLY DATE RENDERING PATH IN THE APP.
 *
 * Every date or time that reaches a screen, an email, a PDF or a CSV goes
 * through one of the fmt* helpers below. They are all pinned to BUSINESS_TZ.
 * An ESLint rule blocks toLocaleString / toLocaleDateString / toLocaleTimeString
 * and bare Intl.DateTimeFormat anywhere else in the repo, because UTC leaking
 * into the display layer has broken this app three times.
 * ------------------------------------------------------------------------ */

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function fmt(opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(opts);
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: BUSINESS_TZ, ...opts });
    fmtCache.set(key, f);
  }
  return f;
}

/**
 * Accepts an ISO string, a Date or a bare YYYY-MM-DD column. A bare date is
 * read at midday UTC so converting it into Pacific never slides it back a day.
 */
export function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") return new Date(value);
  const at = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

/** "3:40 PM" */
export const fmtTime = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  return d ? fmt({ hour: "numeric", minute: "2-digit" }).format(d) : fallback;
};

/** "Jul 31, 2026" */
export const fmtDate = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  return d ? fmt({ month: "short", day: "numeric", year: "numeric" }).format(d) : fallback;
};

/** "Fri, Jul 31, 2026" */
export const fmtFullDate = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  return d
    ? fmt({ weekday: "short", month: "short", day: "numeric", year: "numeric" }).format(d)
    : fallback;
};

/** "July 31, 2026", for customer facing documents. */
export const fmtLongDate = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  return d ? fmt({ month: "long", day: "numeric", year: "numeric" }).format(d) : fallback;
};

/** "Fri, Jul 31, 3:40 PM" */
export const fmtDateTime = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  return d
    ? fmt({
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(d)
    : fallback;
};

/** "Fri, Jul 31, 2026, 3:40 PM", the long form for logs and emails. */
export const fmtFullDateTime = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  return d
    ? fmt({
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(d)
    : fallback;
};

/** "Monday, August 3", the greeting line at the top of a portal. */
export const fmtGreetingDate = (value: Date | string = new Date()) =>
  fmt({ weekday: "long", month: "long", day: "numeric" }).format(toDate(value) ?? new Date());

/** "Mon, Aug 3" */
export const fmtWeekdayDate = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  return d ? fmt({ weekday: "short", month: "short", day: "numeric" }).format(d) : fallback;
};

/** "Monday" */
export const fmtWeekday = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  return d ? fmt({ weekday: "long" }).format(d) : fallback;
};

/**
 * "6th", the day of the month on its own.
 *
 * Used only on the homeowner's page, where a full date reads as a stopwatch.
 * A bare ordinal says which day money landed without starting a clock.
 */
export const fmtOrdinalDay = (value: Date | string | null | undefined, fallback = "") => {
  const d = toDate(value);
  if (!d) return fallback;
  const day = Number(fmt({ day: "numeric" }).format(d));
  const tens = day % 100;
  const suffix =
    tens >= 11 && tens <= 13
      ? "th"
      : day % 10 === 1
        ? "st"
        : day % 10 === 2
          ? "nd"
          : day % 10 === 3
            ? "rd"
            : "th";
  return `${day}${suffix}`;
};

/** "Jul 31", the short stamp used on dense lists. */
export const fmtShortDate = (value: Date | string | null | undefined, fallback = "Not set") => {
  const d = toDate(value);
  if (!d) return fallback;
  const sameYear = businessDateOf(d).slice(0, 4) === todayISO().slice(0, 4);
  return sameYear
    ? fmt({ month: "short", day: "numeric" }).format(d)
    : fmt({ month: "short", day: "numeric", year: "numeric" }).format(d);
};

/** Whole business days between a business-local date string and today. */
export function businessDaysAgo(value: Date | string): number {
  const then = businessDateOf(value);
  const [y1, m1, d1] = then.split("-").map(Number);
  const [y2, m2, d2] = todayISO().split("-").map(Number);
  const a = Date.UTC(y1 ?? 1970, (m1 ?? 1) - 1, d1 ?? 1);
  const b = Date.UTC(y2 ?? 1970, (m2 ?? 1) - 1, d2 ?? 1);
  return Math.round((b - a) / 86400000);
}

/**
 * "just now", "18 min", "Today, 2:14 PM", "Yesterday, 9:02 AM", "Jul 29".
 * Minutes win inside the first hour even across midnight.
 */
export function fmtRelative(value: Date | string | null | undefined, fallback = "Not set"): string {
  const d = toDate(value);
  if (!d) return fallback;
  const minutes = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (minutes < 60) return minutes < 1 ? "just now" : `${minutes} min`;
  const days = businessDaysAgo(d);
  if (days <= 0) return `Today, ${fmtTime(d)}`;
  if (days === 1) return `Yesterday, ${fmtTime(d)}`;
  return fmtShortDate(d);
}
